import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { hrApi } from "../../services/hrApi";
import HRPeopleFilters from "../../components/hr/HRPeopleFilters";
import HRPeopleTable from "../../components/hr/HRPeopleTable";
import { useAccess } from "../../context/AccessContext";
import { hasModule, hasPermission } from "../../utils/accessHelpers";


const defaultForm = {
  user_id: "",
  party_id: "",
  person_type: "employee",
  employee_code: "",
  first_name: "",
  last_name: "",
  display_name: "",
  email_work: "",
  email_private: "",
  phone_mobile: "",
  status: "active",
};

const defaultEditForm = {
  user_id: "",
  party_id: "",
  person_type: "employee",
  employee_code: "",
  first_name: "",
  last_name: "",
  display_name: "",
  email_work: "",
  email_private: "",
  phone_mobile: "",
  phone_private: "",
  phone_work: "",
  date_of_birth: "",
  city: "",
  country: "",
  status: "active",
};

const defaultFilters = {
  q: "",
  status: "",
  person_type: "",
  department_id: "",
  team_id: "",
  position_id: "",
};

function PersonModal({
  open,
  title,
  form,
  setForm,
  onSubmit,
  onClose,
  saving,
  submitLabel,
  includeExtendedFields = false,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-700 shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-neutral-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-black dark:hover:text-white text-xl"
          >
            ✕
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              className="border rounded-xl px-3 py-2 bg-white dark:bg-neutral-950"
              placeholder="First name"
              value={form.first_name}
              onChange={(e) =>
                setForm((s) => ({ ...s, first_name: e.target.value }))
              }
              required
            />

            <input
              className="border rounded-xl px-3 py-2 bg-white dark:bg-neutral-950"
              placeholder="Last name"
              value={form.last_name}
              onChange={(e) =>
                setForm((s) => ({ ...s, last_name: e.target.value }))
              }
              required
            />

            <input
              className="border rounded-xl px-3 py-2 bg-white dark:bg-neutral-950"
              placeholder="Display name"
              value={form.display_name}
              onChange={(e) =>
                setForm((s) => ({ ...s, display_name: e.target.value }))
              }
            />

            <input
              className="border rounded-xl px-3 py-2 bg-white dark:bg-neutral-950"
              placeholder="Employee code"
              value={form.employee_code}
              onChange={(e) =>
                setForm((s) => ({ ...s, employee_code: e.target.value }))
              }
            />

            <select
              className="border rounded-xl px-3 py-2 bg-white dark:bg-neutral-950"
              value={form.person_type}
              onChange={(e) =>
                setForm((s) => ({ ...s, person_type: e.target.value }))
              }
            >
              <option value="employee">Employee</option>
              <option value="contractor">Contractor</option>
              <option value="freelancer">Freelancer</option>
              <option value="intern">Intern</option>
              <option value="consultant">Consultant</option>
              <option value="external_partner">External partner</option>
              <option value="former_employee">Former employee</option>
              <option value="candidate">Candidate</option>
            </select>

            <select
              className="border rounded-xl px-3 py-2 bg-white dark:bg-neutral-950"
              value={form.status}
              onChange={(e) =>
                setForm((s) => ({ ...s, status: e.target.value }))
              }
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="archived">Archived</option>
            </select>

            <input
              className="border rounded-xl px-3 py-2 bg-white dark:bg-neutral-950"
              placeholder="Work email"
              value={form.email_work}
              onChange={(e) =>
                setForm((s) => ({ ...s, email_work: e.target.value }))
              }
            />

            <input
              className="border rounded-xl px-3 py-2 bg-white dark:bg-neutral-950"
              placeholder="Private email"
              value={form.email_private}
              onChange={(e) =>
                setForm((s) => ({ ...s, email_private: e.target.value }))
              }
            />

            <input
              className="border rounded-xl px-3 py-2 bg-white dark:bg-neutral-950"
              placeholder="Mobile phone"
              value={form.phone_mobile}
              onChange={(e) =>
                setForm((s) => ({ ...s, phone_mobile: e.target.value }))
              }
            />

            {includeExtendedFields && (
              <>
                <input
                  className="border rounded-xl px-3 py-2 bg-white dark:bg-neutral-950"
                  placeholder="Private phone"
                  value={form.phone_private}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, phone_private: e.target.value }))
                  }
                />

                <input
                  className="border rounded-xl px-3 py-2 bg-white dark:bg-neutral-950"
                  placeholder="Work phone"
                  value={form.phone_work}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, phone_work: e.target.value }))
                  }
                />

                <input
                  type="date"
                  className="border rounded-xl px-3 py-2 bg-white dark:bg-neutral-950"
                  value={form.date_of_birth}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, date_of_birth: e.target.value }))
                  }
                />

                <input
                  className="border rounded-xl px-3 py-2 bg-white dark:bg-neutral-950"
                  placeholder="City"
                  value={form.city}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, city: e.target.value }))
                  }
                />

                <input
                  className="border rounded-xl px-3 py-2 bg-white dark:bg-neutral-950 md:col-span-2"
                  placeholder="Country"
                  value={form.country}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, country: e.target.value }))
                  }
                />
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-xl bg-black text-white disabled:opacity-60"
            >
              {saving ? "Saving..." : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function HRPeoplePage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [people, setPeople] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [positions, setPositions] = useState([]);

  const [filters, setFilters] = useState(defaultFilters);

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(defaultForm);

  const [showEdit, setShowEdit] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState(defaultEditForm);

  const { access, loading: accessLoading } = useAccess();

  const canSeeHR = hasModule(access, "hr");
  const canReadPeople = hasPermission(access, "hr.people.read");
  const canWritePeople = hasPermission(access, "hr.people.write");

  const queryParams = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) p[k] = v;
    });
    return p;
  }, [filters]);

  async function loadAll() {
    try {
      setLoading(true);

      const [peopleRes, depRes, teamRes, posRes] = await Promise.all([
        hrApi.getPeople(queryParams),
        hrApi.getDepartments(),
        hrApi.getTeams(),
        hrApi.getPositions(),
      ]);

      setPeople(peopleRes.data?.data || []);
      setDepartments(depRes.data?.data || []);
      setTeams(teamRes.data?.data || []);
      setPositions(posRes.data?.data || []);
    } catch (err) {
      console.error("Failed to load HR people page:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, [JSON.stringify(queryParams)]);

  async function handleCreatePerson(e) {
    e.preventDefault();

    try {
      setSaving(true);

      await hrApi.createPerson({
        ...form,
        user_id: form.user_id || null,
        party_id: form.party_id || null,
      });

      setShowCreate(false);
      setForm(defaultForm);
      await loadAll();
    } catch (err) {
      console.error("Failed to create person:", err);
      alert(err?.response?.data?.message || "Failed to create person.");
    } finally {
      setSaving(false);
    }
  }

  function openEditModal(person) {
    setEditingPerson(person);
    setEditForm({
      user_id: person.user_id || "",
      party_id: person.party_id || "",
      person_type: person.person_type || "employee",
      employee_code: person.employee_code || "",
      first_name: person.first_name || "",
      last_name: person.last_name || "",
      display_name: person.display_name || "",
      email_work: person.email_work || "",
      email_private: person.email_private || "",
      phone_mobile: person.phone_mobile || "",
      phone_private: person.phone_private || "",
      phone_work: person.phone_work || "",
      date_of_birth: person.date_of_birth || "",
      city: person.city || "",
      country: person.country || "",
      status: person.status || "active",
    });
    setShowEdit(true);
  }

  async function handleEditPerson(e) {
    e.preventDefault();
    if (!editingPerson?.id) return;

    try {
      setEditSaving(true);

      await hrApi.updatePerson(editingPerson.id, {
        ...editForm,
        user_id: editForm.user_id || null,
        party_id: editForm.party_id || null,
        date_of_birth: editForm.date_of_birth || null,
      });

      setShowEdit(false);
      setEditingPerson(null);
      await loadAll();
    } catch (err) {
      console.error("Failed to update person:", err);
      alert(err?.response?.data?.message || "Failed to update person.");
    } finally {
      setEditSaving(false);
    }
  }

  async function handleArchivePerson(personId) {
    const confirmed = window.confirm("Archive this person?");
    if (!confirmed) return;

    try {
      await hrApi.archivePerson(personId);
      await loadAll();
    } catch (err) {
      console.error("Failed to archive person:", err);
      alert(err?.response?.data?.message || "Failed to archive person.");
    }
  }

  if (accessLoading) {
  return <div className="p-6">Loading access...</div>;
}

if (!canSeeHR || !canReadPeople) {
  return <div className="p-6 text-red-600">Access denied.</div>;
}

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">People Directory</h1>
          <p className="text-sm text-gray-500">
            Search, filter and manage people across HR.
          </p>
        </div>

        {canWritePeople && (
  <button
    onClick={() => setShowCreate(true)}
    className="px-4 py-2 rounded-xl bg-black text-white"
  >
    Add person
  </button>
)}
      </div>

      <HRPeopleFilters
        filters={filters}
        onChange={setFilters}
        onReset={() => setFilters(defaultFilters)}
        departments={departments}
        teams={teams}
        positions={positions}
      />

      <HRPeopleTable
  people={people}
  loading={loading}
  onRowClick={(person) => navigate(`/hr/people/${person.id}`)}
  onEdit={canWritePeople ? openEditModal : undefined}
  onArchive={canWritePeople ? (person) => handleArchivePerson(person.id) : undefined}
/>

    {canWritePeople && (
      <PersonModal
        open={showCreate}
        title="Create person"
        form={form}
        setForm={setForm}
        onSubmit={handleCreatePerson}
        onClose={() => setShowCreate(false)}
        saving={saving}
        submitLabel="Create person"
      />
    )}

      <PersonModal
        open={showEdit}
        title={`Edit person${editingPerson ? ` · ${editingPerson.full_name || `${editingPerson.first_name || ""} ${editingPerson.last_name || ""}`.trim()}` : ""}`}
        form={editForm}
        setForm={setEditForm}
        onSubmit={handleEditPerson}
        onClose={() => {
          setShowEdit(false);
          setEditingPerson(null);
        }}
        saving={editSaving}
        submitLabel="Save changes"
        includeExtendedFields
      />
    </div>
  );
}